export interface OfficerRole {
  id: number;
  name: string;
  sortOrder: number;
}

// In services/officerRoles.ts
export const getAllOfficerRoles = async (): Promise<OfficerRole[]> => {
  try {
    const response = await fetch('/api/officerRoles', {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();

    // Validate the data structure
    if (!Array.isArray(data)) {
      console.error('API returned non-array:', typeof data, data);
      return [];
    }

    // Ensure each role has required fields
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const validatedRoles = data.map((role: any, index: number) => ({
      id: role.id || index,
      name: role.name || 'Unnamed Role',
      sortOrder: role.sortOrder !== undefined ? role.sortOrder : index,
    }));

    return validatedRoles;
  } catch (error) {
    console.error('Service error in getAllOfficerRoles:', error);
    throw error;
  }
};

export async function addOfficerRole(role: Partial<OfficerRole>) {
  const response = await fetch('/api/officerRoles', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(role),
  });

  if (!response.ok) {
    throw new Error(`Failed to add officer role: ${response.status}`);
  }

  return response.json(); // Parse JSON response
}

export async function updateOfficerRole(role: OfficerRole) {
  const response = await fetch(`/api/officerRoles/${role.id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(role),
  });

  if (!response.ok) {
    throw new Error(`Failed to update officer role: ${response.status}`);
  }

  return response.json(); // Parse JSON response
}

export async function deleteOfficerRole(role: OfficerRole) {
  return await fetch(`/api/officerRoles/${role.id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export const updateSortOrder = async (payload: {
  roles: { id: number; sort_order: number }[];
}) => {
  // Validate payload structure
  if (!payload || !Array.isArray(payload.roles)) {
    console.error('Service: Invalid payload structure', payload);
    throw new Error('Invalid payload: roles must be an array');
  }

  // Check each role in the payload
  payload.roles.forEach((role, index) => {
    if (!role.id) {
      console.error(`Service: Role ${index} missing id`);
    }
    if (role.sort_order === undefined || role.sort_order === null) {
      console.error(`Service: Role ${index} missing sort_order`);
    }
  });

  try {
    const response = await fetch('/api/officerRoles/sort-order', {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Service: API Error Response:', errorText);
      throw new Error(`Failed to update sort order: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error('Service: Request failed:', error);
    throw error;
  }
};